import React, { ReactNode, useRef, useEffect, useState } from 'react';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  width?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (row: T) => string | number;
  emptyText?: string;
  minWidth?: string;
  renderExpanded?: (row: T) => ReactNode;
}

function DataTable<T>({
  columns,
  data,
  rowKey,
  emptyText = 'Нет данных',
  minWidth = '600px',
  renderExpanded,
}: DataTableProps<T>) {
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const stickyBarRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [stickyStyle, setStickyStyle] = useState<React.CSSProperties>({ display: 'none' });
  const [expandedKeys, setExpandedKeys] = useState<Set<string | number>>(new Set());
  const syncing = useRef(false);

  const toggleExpand = (key: string | number) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const updateSticky = () => {
    const wrap = tableWrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;

    const rect = wrap.getBoundingClientRect();
    const viewH = window.innerHeight;

    inner.style.width = wrap.scrollWidth + 'px';

    const visible = rect.bottom > viewH && rect.top < viewH;
    setStickyStyle(visible ? {
      display: 'block',
      position: 'fixed',
      bottom: 0,
      left: rect.left,
      width: rect.width,
      overflowX: 'scroll',
      overflowY: 'hidden',
      height: '16px',
      zIndex: 100,
      background: 'var(--bg-primary, #fff)',
      borderTop: '1px solid var(--border-light, #e0e0e0)',
      boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
    } : { display: 'none' });
  };

  useEffect(() => {
    const wrap = tableWrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(updateSticky);
    ro.observe(wrap);
    window.addEventListener('scroll', updateSticky, { passive: true });
    window.addEventListener('resize', updateSticky, { passive: true });
    updateSticky();
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', updateSticky);
      window.removeEventListener('resize', updateSticky);
    };
  }, [data, columns]);

  const onTableScroll = () => {
    if (syncing.current) return;
    syncing.current = true;
    if (stickyBarRef.current && tableWrapRef.current)
      stickyBarRef.current.scrollLeft = tableWrapRef.current.scrollLeft;
    syncing.current = false;
  };

  const onStickyScroll = () => {
    if (syncing.current) return;
    syncing.current = true;
    if (tableWrapRef.current && stickyBarRef.current)
      tableWrapRef.current.scrollLeft = stickyBarRef.current.scrollLeft;
    syncing.current = false;
  };

  const totalCols = columns.length + (renderExpanded ? 1 : 0);

  return (
    <>
      <div className="data-table-wrap" ref={tableWrapRef} onScroll={onTableScroll}>
        <table className="data-table" style={{ minWidth }}>
          <thead>
            <tr>
              {renderExpanded && <th style={{ width: '36px' }} />}
              {columns.map((col) => (
                <th key={col.key} style={col.width ? { width: col.width } : undefined}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row) => {
              const key = rowKey(row);
              const isExpanded = expandedKeys.has(key);
              return (
                <React.Fragment key={key}>
                  <tr
                    className={renderExpanded ? (isExpanded ? 'row-expandable row-expanded' : 'row-expandable') : ''}
                    onClick={renderExpanded ? () => toggleExpand(key) : undefined}
                  >
                    {renderExpanded && (
                      <td className="expand-toggle-cell">
                        <span className={isExpanded ? 'expand-arrow expanded' : 'expand-arrow'}>›</span>
                      </td>
                    )}
                    {columns.map((col) => (
                      <td key={col.key}>
                        {col.render ? col.render(row) : String((row as any)[col.key] ?? '-')}
                      </td>
                    ))}
                  </tr>
                  {renderExpanded && isExpanded && (
                    <tr className="expanded-content-row">
                      <td colSpan={totalCols} className="expanded-content-cell">
                        <div className="expanded-row-body">
                          {renderExpanded(row)}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {data.length === 0 && <div className="data-table-empty">{emptyText}</div>}
      </div>

      <div ref={stickyBarRef} onScroll={onStickyScroll} style={stickyStyle}>
        <div ref={innerRef} style={{ height: '1px' }} />
      </div>
    </>
  );
}

export default DataTable;
